import { useState, type ReactNode } from 'react';
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import type { ServiceDetail } from '@/api/services';
import {
  useCreateManualInvoice,
  useCreateStatementInvoice,
  useCreateFeeInvoice,
  useCreateServiceNote,
} from '@/api/services-mutations';
import { showStatementTab } from './service-tab-gating';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

export interface ActionItem {
  key: string;
  label: string;
  tone?: 'danger';
  onClick: () => void;
}

/**
 * Billing + note actions (add note, renewal/statement/fee invoice) as overflow
 * menu items + their dialogs. Returned to the lifecycle action bar so everything
 * collapses into one "More" menu. Invoice actions gate to active services. The
 * USER runs these live (data safety).
 */
export function useServiceBillingActions(detail: ServiceDetail): { items: ActionItem[]; dialogs: ReactNode } {
  const id = detail.service_id;
  const toast = useToast();
  const [open, setOpen] = useState<null | 'note' | 'renew' | 'statement' | 'fee'>(null);
  const [text, setText] = useState('');
  const [fee, setFee] = useState({ start_date: '', end_date: '', fee_amount: '', fee_name: '' });

  const note = useCreateServiceNote(id);
  const renew = useCreateManualInvoice(id);
  const statement = useCreateStatementInvoice(id);
  const feeInvoice = useCreateFeeInvoice(id);

  const isActive = detail.service_status === 'active';
  const close = () => { setOpen(null); setText(''); setFee({ start_date: '', end_date: '', fee_amount: '', fee_name: '' }); };
  const run = (p: Promise<unknown>, ok: string) => p.then(() => { toast.success(ok); close(); }).catch((e) => toast.error(errMsg(e)));

  const items: ActionItem[] = [{ key: 'note', label: 'Add note', onClick: () => setOpen('note') }];
  if (isActive) {
    items.push({ key: 'renew-inv', label: 'Create renewal invoice', onClick: () => setOpen('renew') });
    if (showStatementTab(detail)) items.push({ key: 'stmt-inv', label: 'Create statement invoice', onClick: () => setOpen('statement') });
    items.push({ key: 'fee-inv', label: 'Create fee invoice', onClick: () => setOpen('fee') });
  }

  const dialogs = (
    <>
      <TextDialog
        open={open === 'note'} title="Add note" label="Note" confirmLabel="Add" pending={note.isPending}
        value={text} onChange={setText} onClose={close}
        onSubmit={() => run(note.mutateAsync(text.trim()), 'Note added')}
      />
      <ConfirmDialog
        open={open === 'renew'} onOpenChange={(o) => !o && close()}
        title="Create renewal invoice" description={`Raise a renewal invoice for "${detail.service_name}"?`}
        confirmLabel="Create" loading={renew.isPending}
        onConfirm={() => run(renew.mutateAsync(''), 'Renewal invoice created')}
      />
      <ConfirmDialog
        open={open === 'statement'} onOpenChange={(o) => !o && close()}
        title="Create statement invoice" description={`Raise a statement (đối soát) invoice for "${detail.service_name}"?`}
        confirmLabel="Create" loading={statement.isPending}
        onConfirm={() => run(statement.mutateAsync(''), 'Statement invoice created')}
      />
      <FeeDialog
        open={open === 'fee'} pending={feeInvoice.isPending} value={fee} onChange={setFee} onClose={close}
        onSubmit={() => run(
          feeInvoice.mutateAsync({ start_date: fee.start_date, end_date: fee.end_date, fee_amount: Number(fee.fee_amount) || 0, fee_name: fee.fee_name || undefined }),
          'Fee invoice created',
        )}
      />
    </>
  );

  return { items, dialogs };
}

function TextDialog({ open, title, label, confirmLabel, pending, value, onChange, onClose, onSubmit }: {
  open: boolean; title: string; label: string; confirmLabel: string; pending: boolean;
  value: string; onChange: (v: string) => void; onClose: () => void; onSubmit: () => void;
}) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <DialogBody>
          <Field label={label} required>
            {(p) => <Input {...p} value={value} onChange={(e) => onChange(e.target.value)} placeholder="…" autoFocus />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Close</Button>
          <Button variant="primary" disabled={!value.trim() || pending} onClick={onSubmit}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeeDialog({ open, pending, value, onChange, onClose, onSubmit }: {
  open: boolean; pending: boolean;
  value: { start_date: string; end_date: string; fee_amount: string; fee_name: string };
  onChange: (v: { start_date: string; end_date: string; fee_amount: string; fee_name: string }) => void;
  onClose: () => void; onSubmit: () => void;
}) {
  if (!open) return null;
  const set = (k: keyof typeof value, v: string) => onChange({ ...value, [k]: v });
  const valid = value.start_date && value.end_date && Number(value.fee_amount) > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create fee invoice</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Fee name">{(p) => <Input {...p} value={value.fee_name} onChange={(e) => set('fee_name', e.target.value)} placeholder="e.g. Setup fee" />}</Field>
            <Field label="Amount" required>{(p) => <NumericInput {...p} value={value.fee_amount} onChange={(e) => set('fee_amount', e.target.value)} />}</Field>
            <Field label="From" required>{(p) => <DateInput {...p} value={value.start_date} onChange={(e) => set('start_date', e.target.value)} />}</Field>
            <Field label="To" required>{(p) => <DateInput {...p} value={value.end_date} onChange={(e) => set('end_date', e.target.value)} />}</Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Close</Button>
          <Button variant="primary" disabled={!valid || pending} onClick={onSubmit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
